import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin, ArrowUpRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { SectionHead } from "./Reveal";
import { PROJECTS, PROJECT_CATEGORIES } from "../data/site";

export const Projects = () => {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);

  const visible = filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <section id="projects" data-testid="projects-section" className="bg-[#050505] px-6 py-24 md:px-12 md:py-32">
      <div className="mx-auto max-w-7xl">
        <SectionHead
          id="projects"
          overline="Chapter 06 — Selected Work"
          title="Projects"
          desc="A look at the structures we design, fabricate and erect across Hyderabad and Telangana."
        />

        <div className="mt-12 flex flex-wrap gap-2" data-testid="project-filters">
          {["All", ...PROJECT_CATEGORIES].map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setFilter(c)}
              className={`border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] transition-all ${
                filter === c
                  ? "border-[#0055FF] bg-[#0055FF] text-white"
                  : "border-white/15 text-[#A3A3A3] hover:border-white/40 hover:text-white"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <motion.div layout className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => (
              <motion.article
                layout
                key={p.id}
                data-testid={`project-card-${p.id}`}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                className="group border border-white/10 bg-[#0A0A0A]"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={p.img}
                    alt={p.title}
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute bottom-3 left-3 bg-[#0055FF] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white">
                    {p.category}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-lg font-semibold tracking-tight">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#A3A3A3]">{p.desc}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-xs text-white/50">
                      <MapPin size={13} className="text-[#3377FF]" /> {p.location}
                    </span>
                    <button
                      data-testid={`view-project-${p.id}`}
                      onClick={() => setActive(p)}
                      className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-[#3377FF] transition-colors hover:text-white"
                    >
                      View Project <ArrowUpRight size={14} />
                    </button>
                  </div>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent className="max-w-2xl border-white/10 bg-[#0A0A0A] text-white" data-testid="project-modal">
          {active && (
            <>
              <div className="relative overflow-hidden">
                <img src={active.img} alt={active.title} className="aspect-[16/9] w-full object-cover" />
              </div>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-bold tracking-tight" data-testid="project-modal-title">
                  {active.title}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm text-[#A3A3A3]">
                <p className="flex items-center gap-2">
                  <span className="bg-[#0055FF] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-white">
                    {active.category}
                  </span>
                  <span className="flex items-center gap-1.5 text-white/60">
                    <MapPin size={13} className="text-[#3377FF]" /> {active.location}
                  </span>
                </p>
                <p className="leading-relaxed">{active.desc}</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
